import React, { ReactNode, FC, useContext, useMemo, ComponentType, MouseEvent } from 'react';
import { ThemeContext } from 'styled-components';
import getNodeText from 'ia-react-core/~/utils/components/_getNodeText';

import MainFooterButton, { MainFooterButtonProps } from '../../buttons/MainFooterButton';

import FooterDynamicLinks from './FooterDynamicLinks';
import FooterStaticLinks from './FooterStaticLinks';

import { StyledFooterLinks, StyledFooterLinksWrapper, StyledFooterButtons } from './FooterLinks.styles';
import FooterLanguageButton from '../_FooterLanguageButton';

export interface FooterLinksProps {
  /** List of external links. Prefer `linksUrl` when possible. */
  links?: {
    url?: string;
    key?: string;
    text: ReactNode;
    target?: '_self' | '_blank' | '_parent' | '_top';
    onClick?: (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  }[];
  /** Links for the Sitecore service returning the footer links. */
  linksUrl?: string;
  linksDomain?: string;
  /** Callback triggered when the language button is clicked.  @param {string} nextLanguage The next language code */
  onLanguageChange?: (newLocale: 'fr' | 'en') => void;
  /** Removes the language button */
  noLanguageButton?: boolean;
  /** List of buttons to display to the right of the links. */
  buttons?: {
    text?: ReactNode;
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
    RightIcon?: ComponentType;
    props?: MainFooterButtonProps;
  }[];
  /** Display links vertically, one above the other  */
  vertical?: boolean;
  noLinksLoader?: boolean;
}

const FooterLinks: FC<FooterLinksProps> = ({ links, linksUrl, noLinksLoader, buttons, linksDomain, onLanguageChange, noLanguageButton, vertical }) => {
  const theme = useContext(ThemeContext);

  const resolvedNoLanguageButton = useMemo(() => {
    if (typeof noLanguageButton !== 'undefined') {
      return noLanguageButton;
    }

    return theme.layout.noFooterLanguageButton;
  }, [noLanguageButton, theme.layout.noFooterLanguageButton]);

  if (!linksUrl && !links && resolvedNoLanguageButton) {
    return null;
  }

  return (
    <StyledFooterLinksWrapper vertical={vertical} data-testid="FooterLinks">
      <StyledFooterLinks vertical={vertical}>
        {linksUrl && (
          <FooterDynamicLinks
            noLinksLoader={noLinksLoader}
            linksUrl={linksUrl}
            linksDomain={linksDomain}
          />
        )}
        {links && (
          <FooterStaticLinks links={links} />
        )}
      </StyledFooterLinks>
      {(buttons || !resolvedNoLanguageButton) && (
        <StyledFooterButtons vertical={vertical}>
          {buttons && buttons.map((button) => (
            <MainFooterButton
              key={getNodeText(button.text)}
              onClick={button.onClick}
              RightIcon={button.RightIcon}
              {...button.props}
            >
              {button.text}
            </MainFooterButton>
          ))}
          {!resolvedNoLanguageButton && (
            <FooterLanguageButton onLanguageChange={onLanguageChange} />
          )}
        </StyledFooterButtons>
      )}
    </StyledFooterLinksWrapper>
  );
};

export default FooterLinks;
